import type { Room } from "@rooms/roomsClient";

import {
  AREA_LABEL,
  type MapDisplay,
  type MapDisplays,
  type MapDocument,
  type MapDoor,
  type MapSpace,
  type MapWarning,
  OUTSIDE_LABEL,
} from "./clinicMapClient";
import { doorJoin, doorsOf, otherSide, wallSide } from "./mapGeometry";

export type RoomsById = ReadonlyMap<string, Room>;
/** What naming needs; a screen with no staff session has only the live map's names. */
export type RoomNames = ReadonlyMap<string, { name: string }>;

export const screenName = (display: MapDisplay | undefined) =>
  display?.name ?? "A screen that no longer exists";

export const spaceName = (space: MapSpace | undefined, rooms: RoomNames) => {
  if (!space) return "a space no longer on the map";
  if (space.room_uuid)
    return rooms.get(space.room_uuid)?.name ?? "a room that no longer exists";
  if (space.label) return space.label;
  return space.area ? AREA_LABEL[space.area] : "New space";
};

const spaceById = (doc: MapDocument, uuid: string | null) =>
  doc.spaces.find((s) => s.uuid === uuid);

/** Where a door of `from` leads, as its row in that room's door list. */
export const doorLeadsTo = (
  doc: MapDocument,
  door: MapDoor,
  from: MapSpace,
  rooms: RoomNames,
) => {
  if (door.label) return door.label;
  const join = doorJoin(doc, door);
  if (!join.ok) return "A door off the walls";
  const other = otherSide(join, from);
  if (other) return `To ${spaceName(spaceById(doc, other), rooms)}`;
  return door.outside ? OUTSIDE_LABEL[door.outside] : "Outside";
};

/**
 * Each door on the space's walls and where it leads, in map order. Doors that
 * would read the same (two openings to one hallway) add their wall on the map,
 * then their place along it, so each row names one door.
 */
export const doorLabels = (
  doc: MapDocument,
  space: MapSpace,
  rooms: RoomNames,
): ReadonlyMap<string, string> => {
  const doors = doorsOf(doc, space).map((door) => ({
    door,
    side: wallSide(door, space),
    label: doorLeadsTo(doc, door, space, rooms),
  }));
  const sided = doors.map((d) =>
    d.side && doors.filter((o) => o.label === d.label).length > 1
      ? { ...d, label: `${d.label}, ${d.side} wall` }
      : d,
  );
  return new Map(
    sided.map(({ door, side, label }) => {
      const same = sided.filter((o) => o.label === label);
      if (!side || same.length < 2) return [door.uuid, label];
      const upright = side === "left" || side === "right";
      const at = (d: MapDoor) => (upright ? d.segment.y1 : d.segment.x1);
      const rank = same.filter((o) => at(o.door) < at(door)).length + 1;
      return [
        door.uuid,
        `${label}, #${rank} from the ${upright ? "top" : "left"}`,
      ];
    }),
  );
};

/** Both sides of a door, for the inspector and warnings. */
export const doorTitle = (
  doc: MapDocument,
  door: MapDoor,
  rooms: RoomNames,
) => {
  const join = doorJoin(doc, door);
  if (!join.ok) return door.label ?? "Door";
  const from = spaceName(spaceById(doc, join.space), rooms);
  const to = join.to
    ? spaceName(spaceById(doc, join.to), rooms)
    : door.outside
      ? OUTSIDE_LABEL[door.outside]
      : "outside";
  return `${door.label ? `${door.label}: ` : ""}${from} ↔ ${to}`;
};

/** One line per brian `map_rules.warnings` code. */
export const warningText = (
  warning: MapWarning,
  doc: MapDocument,
  rooms: RoomNames,
  displays: MapDisplays,
) => {
  const room = warning.room_uuid
    ? (rooms.get(warning.room_uuid)?.name ?? "A room that no longer exists")
    : "A room";
  const device = warning.instance_id ?? "A device";
  const door = doc.doors.find((d) => d.uuid === warning.door_uuid);
  switch (warning.code) {
    case "room_gone":
      return "A space's room no longer exists. Remove the space or tie it to another room.";
    case "room_inactive":
      return `${room} is out of service.`;
    case "room_not_on_map":
      return `${room} has a camera but is not on the map.`;
    case "device_not_placed":
      return `${device} in ${room} is not placed on the map.`;
    case "device_elsewhere":
      return `${device} is now in another room, or gone. Move or remove it.`;
    case "door_area_gone":
      return `${room}'s camera has a door drawn that is no longer on its walls. Draw its doors again.`;
    case "doors_not_drawn":
      return `${room}'s doors are not drawn on its camera, so Brian can't see people come and go.`;
    case "entrance_unseen":
      return `${door ? doorTitle(doc, door, rooms) : "An entrance"}: no camera has it drawn, so Brian can't see people come in or leave.`;
    case "screen_gone":
      return "A screen on the map no longer exists. Remove it from the map.";
    case "screen_not_placed":
      return `${screenName(displays.get(warning.display_uuid ?? ""))} is not placed on the map.`;
  }
};
